import React, { useEffect, useState } from "react";
import EzTable from "../../../common/EzTable";
import { EzToast, TOAST_SEVERITY } from "../../../common/EzToast";
import EzNotFound from "../../../common/EzNotFound";
import { Box, Container } from "@mui/material";
import Breadcrumb from "../../../common/Breadcrumb";
import { Button } from "primereact/button";
import { useStyles } from "../Affiliate/style";
import { printTableData } from "../../../utils/printUtils";
import GenerateInvoiceDialog from "./GenerateInvoiceDialog";
import { useNavigate } from "react-router-dom";
import { APP_URLS } from "../../../utils/constant";

const getIconPath = (imgPathDelta = "/no-record.png") =>
  process.env.PUBLIC_URL + imgPathDelta;

const AffiliateInvoice = (props) => {
    const {
    handleViewInvoice
  } = props;
  const navigate = useNavigate();
  const classes = useStyles();
  const {
    savedTableState,
  } = props;

  const [areFiltersVisible, toggleFilterState] = useState(true);
  const [isCopy, setIsCopy] = useState(false);
  const [isPrintError, setIsPrintError] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);

  const {
    fetchAffiliateInvoices,
    tableData,
    isPageLevelError,
    paginationInfo,
    isLoading,
    screenPermissions,
    onTableStateChange,
    exportAffiliateInvoice,
    generateAffiliateInvoice,
    isGenerateInvoiceSuccess,
    isGenerateInvoiceError,
    affiliateDD,
  } = props;

  const breadcrumbD = [
    { path: "/dashboard", label: "Dashboard" },
    { path: "/affiliate", label: "Affiliate" },
    { path: "/affiliate-invoices", label: "Affiliate Invoices" },
  ];

  const convertToTSV = (data) => {
    const headers = data.columns.map((item) => item.colLabel).join("\t");
    const rows = data.rows
      .map((row) => {
        const { id, ...rowWithoutId } = row;
        return Object.values(rowWithoutId).join("\t");
      })
      .join("\n");
    return `${headers}\n${rows}`;
  };

  const copyToClipboard = () => {
    const csv = convertToTSV(tableData);
    navigator.clipboard
      .writeText(csv)
      .then(() => setIsCopy(true))
      .catch((err) => setIsCopy(true));
  };

  useEffect(() => {
    setTimeout(() => setIsCopy(false), 1000);
  }, [isCopy]);

  useEffect(() => {
    if (isPrintError) {
      setTimeout(() => setIsPrintError(false), 3000);
    }
  }, [isPrintError]);

  useEffect(() => {
    if (isGenerateInvoiceSuccess) {
      setShowGenerateDialog(false);
    }
  }, [isGenerateInvoiceSuccess]);

  const handleGenerateInvoice = async (payload) => {
    try {
      await generateAffiliateInvoice(payload);
    } catch (error) {
      console.error("Error generating invoice:", error);
    }
  };

  const handlePrint = () => {
    const success = printTableData({
      title: "Affiliate Invoices",
      columns: tableData?.columns || [],
      rows: tableData?.rows || [],
      excludeColumns: ["id"],
      orientation: "landscape",
    });

    if (!success) {
      setIsPrintError(true);
    }
  };

  const renderPageHeaderSection = () => {
    return (
      <div className="make-relative sub-header">
        <div className="maintitlediv">
          <div className="ml-3">
            <h4 className="pagetitle">Affiliate Invoices</h4>
            <Button
              label={areFiltersVisible ? "Advanced Filter" : "Advanced Filter"}
              icon={areFiltersVisible ? "pi pi-filter-slash" : "pi pi-filter"}
              className={`p-button-text p-button-secondary shadow-btn`}
              onClick={() => {
                toggleFilterState(!areFiltersVisible);
              }}
            />
          </div>
          <div className="buttonarea">
            {screenPermissions?.create && (
              <Button
                label="Generate Invoice"
                icon="pi pi-plus"
                className="p-button-raised"
                onClick={() => setShowGenerateDialog(true)}
                style={{ marginRight: "10px" }}
              />
            )}
            <div className="copy-excel-print-group">
              <Button
                label={"Copy"}
                className={` p-button-raised`}
                style={{ borderRadius: "0px" }}
                onClick={copyToClipboard}
              />
              <Button
                label={"Excel"}
                className={` p-button-raised`}
                style={{ borderRadius: "0px" }}
                onClick={exportAffiliateInvoice}
              />
              <Button
                label={"Print"}
                className={` p-button-raised`}
                style={{ borderRadius: "0px" }}
                onClick={handlePrint}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const shouldRenderFullPageError = () => isPageLevelError;
  const shouldRenderRecords = () => tableData?.columns?.length > 0;
  const shouldRenderNotFoundView = () =>
    !shouldRenderRecords() && !shouldRenderFullPageError();

  const getToastProps = () => {
    if (isCopy) {
      return {
        severity: TOAST_SEVERITY.SUCCESS,
        toastTitle: "Data copied to clipboard!",
        shouldShowToast: true,
      };
    }

    if (isPrintError) {
      return {
        severity: TOAST_SEVERITY.ERROR,
        toastTitle: "Please allow popups to use the print feature.",
        shouldShowToast: true,
      };
    }

    if (isGenerateInvoiceSuccess) {
      return {
        severity: TOAST_SEVERITY.SUCCESS,
        toastTitle: "Affiliate invoice(s) generated successfully!",
        shouldShowToast: true,
      };
    }

    if (isGenerateInvoiceError) {
      return {
        severity: TOAST_SEVERITY.ERROR,
        toastTitle: "Failed to generate invoice. Please try again.",
        shouldShowToast: true,
      };
    }

    if (props.isUpdateInvoiceSuccess) {
      return {
        severity: TOAST_SEVERITY.SUCCESS,
        toastTitle: "Invoice status updated successfully!",
        shouldShowToast: true,
      };
    }

    if (props.isUpdateInvoiceError) {
      return {
        severity: TOAST_SEVERITY.ERROR,
        toastTitle: "Failed to update invoice status. Please try again.",
        shouldShowToast: true,
      };
    }

    return {};
  };

  const getInitialFilters = () => {
    const defaultFilters = {
      global: { value: null },
      id: { value: null, filterKey: "id" },
      affiliateName: { value: null, filterKey: "affiliateName" },
      startDate: { value: null, filterKey: "startDate" },
      endDate: { value: null, filterKey: "endDate" },
      invoiceDate: { value: null, filterKey: "invoiceDate" },
      dueDate: { value: null, filterKey: "dueDate" },
      totalCommissionAmount: { value: null, filterKey: "totalCommissionAmount" },
      confirmedReservationCount: { value: null, filterKey: "confirmedReservationCount" },
      cancelledReservationCount: { value: null, filterKey: "cancelledReservationCount" },
      noshowReservationCount: { value: null, filterKey: "noshowReservationCount" },
      totalPaidAmount: { value: null, filterKey: "totalPaidAmount" },
      invoiceStatus: { value: null, filterKey: "invoiceStatus" },
      createdDate: { value: null, filterKey: "createdDate" },
    };

    if (savedTableState?.filters) {
      const savedFilters = savedTableState.filters;
      Object.keys(savedFilters).forEach((key) => {
        if (defaultFilters[key]) {
          defaultFilters[key] = {
            ...defaultFilters[key],
            ...savedFilters[key],
          };
        }
      });
    }

    return defaultFilters;
  };

  const filters = getInitialFilters();

  const handleDownloadInvoice = (rowData) => {
    props.downloadAffiliateInvoice(rowData.id);
  };

  const handleUpdateStatus = (rowData) => {
    const id = rowData?.id || rowData;
    navigate(APP_URLS.AFFILIATE_INVOICE.EDIT.replace(":invoiceId", id));
  };

  const tableProps = {
    areFiltersVisible,
    columns: tableData?.columns,
    paginationInfo,
    screenPermissions,
    value: tableData?.rows,
    emptyMessage: "No records found.",
    filters,
    loadLazyData: fetchAffiliateInvoices,
    loading: isLoading,
    showMoreActions: false,
    sortField: savedTableState?.sortField || "id",
    initialSortOrder: savedTableState?.sortOrder || -1,
    initialFirst: savedTableState?.first || 0,
    initialRows: savedTableState?.rows || 10,
    onTableStateChange,
    setDeleteConfirmationPopupVisibility: () => {},
    showViewButton: true,
    onViewRecord: handleViewInvoice,
    showCopyButton: false,
    showDeleteButton: false,
    showEditButton: true,
    onEditRecord: handleUpdateStatus,
    showDownloadButton: true,
    onDownloadClick: handleDownloadInvoice,
  };

  return (
    <>
      <Container maxWidth="lg">
        <Breadcrumb list={breadcrumbD} />
        <EzToast {...getToastProps()} />
        <div className={`containerbox ${classes.bg}`}>
          {renderPageHeaderSection()}
          <div className="mTop15">
            {shouldRenderFullPageError() && (
              <EzNotFound
                messageStyle={{ marginLeft: "-70px" }}
                bgImgUrl={getIconPath("something-broke.png")}
                message={"Oops! Something broke, we are working on a fix."}
              />
            )}
            {shouldRenderRecords() && (
              <Box>
                <EzTable {...tableProps} />
              </Box>
            )}
            {shouldRenderNotFoundView() && (
              <EzNotFound
                bgImgUrl={getIconPath()}
                message={"No Affiliate Invoice records to show"}
              />
            )}
          </div>
        </div>

        <GenerateInvoiceDialog
          visible={showGenerateDialog}
          onHide={() => setShowGenerateDialog(false)}
          onGenerate={handleGenerateInvoice}
          affiliateDD={affiliateDD}
          isGenerating={isLoading}
        />
      </Container>
    </>
  );
};

export default AffiliateInvoice;
